import { useAuthorization } from '@shared/auth/use-authorization';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  HiOutlineCheck,
  HiOutlinePencil,
  HiOutlineXMark,
} from 'react-icons/hi2';
import {
  LiaCalendar,
  LiaPillsSolid,
  LiaThLargeSolid,
} from 'react-icons/lia';
import { Outlet } from 'react-router-dom';

import { Breadcrumbs } from '@/components-new/breadcrumbs';
import { Button } from '@/components-new/button';
import { Page } from '@/components-new/page';
import { PageTitleRow } from '@/components-new/page-title-row';
import { Tab, TabGroup, TabList, TabPanels } from '@/components-new/tab';
import { useClassificationQuery, useUpdateClassification } from '@/features/classifications/api';
import { useTabs } from '@/hooks/use-tabs';
import { useParams } from '@/router';
import { insertIf } from '@/utils/arrays';

type RenameFormValues = { name: string };

const RenameInline = ({
  classificationId,
  currentName,
  onDone,
}: {
  classificationId: number;
  currentName: string;
  onDone: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { register, handleSubmit, reset } = useForm<RenameFormValues>({
    defaultValues: { name: currentName },
  });
  const { ref: rhfRef, ...rest } = register('name', { required: true, maxLength: 255 });
  const { mutate: update, isPending } = useUpdateClassification();

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const onSubmit = ({ name }: RenameFormValues) => {
    update({ id: classificationId, name }, { onSuccess: onDone });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => { if (e.key === 'Escape') { reset({ name: currentName }); onDone(); } }}
      className="flex items-center gap-3"
    >
      <input
        {...rest}
        ref={(el) => { rhfRef(el); inputRef.current = el; }}
        disabled={isPending}
        className="font-serif text-2xl font-bold leading-7 text-primary-950 sm:text-4xl sm:tracking-tight border-b-2 border-secondary-600 bg-transparent outline-none w-72"
      />
      <Button type="submit" color="secondary" disabled={isPending}>
        <HiOutlineCheck />
        Save
      </Button>
      <Button plain type="button" onClick={onDone} disabled={isPending}>
        <HiOutlineXMark />
      </Button>
    </form>
  );
};

const ClassificationLayout = () => {
  const { id } = useParams('/classifications/:id');
  const classificationId = parseInt(id, 10);

  const { checkPolicies } = useAuthorization();
  const canManageClassifications = checkPolicies(['canManageClassifications']);
  const canViewCalendar = checkPolicies(['canViewCalendar']);

  const { data: classification, isLoading } = useClassificationQuery({ id: classificationId });
  const classificationName = classification?.name ?? '';

  const [renaming, setRenaming] = useState(false);

  const { tabs, activeTabIndex, onChange } = useTabs([
    {
      label: <><LiaThLargeSolid />Overview</>,
      href: `/classifications/${id}`,
    },
    {
      label: <><LiaPillsSolid />Drugs</>,
      href: `/classifications/${id}/drugs`,
    },
    ...insertIf(canViewCalendar, {
      label: <><LiaCalendar />Meetings</>,
      href: `/classifications/${id}/meetings`,
    }),
  ]);

  return (
    <Page
      title={classificationName || 'Classification'}
      header={({ title }) => (
        <>
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Classifications', url: '/classifications' },
              { label: classificationName, url: `/classifications/${id}` },
            ]}
          />
          <PageTitleRow title={renaming ? undefined : title}>
            {canManageClassifications && classification && (
              renaming ? (
                <RenameInline
                  classificationId={classificationId}
                  currentName={classificationName}
                  onDone={() => setRenaming(false)}
                />
              ) : (
                <Button plain onClick={() => setRenaming(true)}>
                  <HiOutlinePencil />
                  Rename
                </Button>
              )
            )}
          </PageTitleRow>
        </>
      )}
    >
      <TabGroup selectedIndex={activeTabIndex} onChange={onChange}>
        <TabList>
          {tabs.map((tab) => (
            <Tab key={tab.href}>{tab.label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          <div className="mt-2">
            <Outlet context={{ classification, isLoading }} />
          </div>
        </TabPanels>
      </TabGroup>
    </Page>
  );
};

export default ClassificationLayout;
